// packages/frontend/src/features/recipes/Rating.tsx
//
// Modern numeric-pill rating. Replaces StarRating.
//
// Display mode (no onChange): single pill with the number ("4" or "4.1" for
// fractional averages). Pill colour intensifies with the rating so a glance is
// enough to tell 5/5 from 2/5. Renders nothing when value is null.
//
// Input mode (onChange present): row of six pills — "—" (clear) + 1..5. Click
// to select. ARIA: role=radiogroup, role=radio per button. Click the currently-
// selected pill again to clear.
//
// Why numeric: handles fractional averages (Plan 07's stats.dishRating.avg
// will be a float like 4.1) and integer manual ratings (Plan 08's manual
// rating field is 1|2|3|4|5|null) with the same component, no UI change.
import type { JSX } from "react";
import { useCallback } from "react";

export type RatingValue = 1 | 2 | 3 | 4 | 5 | null;

interface RatingProps {
  value: number | null;
  /** When provided, renders interactive input. Otherwise renders read-only pill. */
  onChange?: (next: RatingValue) => void;
  /** Override input behavior — always render as display. */
  readOnly?: boolean;
  /** Compact (xs) variant for dense rows like RecipeCard. */
  size?: "sm" | "xs";
}

function pillClass(value: number): string {
  // Smooth intensity scale. "Filled" at 4.5+ (would be 5/5 manual or 4.5+ avg).
  if (value >= 4.5) return "bg-emerald-500 text-emerald-950 border-emerald-500";
  if (value >= 3.5) return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
  if (value >= 2.5) return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
  return "bg-emerald-500/5 text-emerald-200/80 border-emerald-500/20";
}

function formatRating(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export default function Rating({ value, onChange, readOnly, size = "sm" }: RatingProps): JSX.Element | null {
  const interactive = onChange !== undefined && !readOnly;

  const handleSelect = useCallback(
    (n: RatingValue) => {
      if (!onChange) return;
      onChange(value === n ? null : n);
    },
    [value, onChange],
  );

  // Display mode — single pill.
  if (!interactive) {
    if (value === null) return null;
    const sizing = size === "xs" ? "h-5 px-2 text-[0.6875rem]" : "h-6 px-2.5 text-xs";
    return (
      <span
        className={`inline-flex items-center justify-center font-semibold tabular-nums border rounded-md ${sizing} ${pillClass(value)}`}
        aria-label={`Rating ${formatRating(value)} of 5`}
        data-testid="rating-display"
      >
        {formatRating(value)}
      </span>
    );
  }

  // Input mode — clear + 1..5 row.
  return (
    <div role="radiogroup" aria-label="Rating" className="inline-flex items-center gap-1" data-testid="rating-input">
      <button
        type="button"
        role="radio"
        aria-checked={value === null}
        aria-label="Clear rating"
        onClick={() => { handleSelect(null); }}
        className={`h-9 px-3 inline-flex items-center justify-center text-xs font-medium border rounded-md transition-colors ${
          value === null
            ? "bg-zinc-800 text-zinc-200 border-zinc-700"
            : "bg-transparent text-zinc-500 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-300"
        }`}
      >
        —
      </button>
      {[1, 2, 3, 4, 5].map((n) => {
        const selected = value === n;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Rate ${n} of 5`}
            onClick={() => { handleSelect(n as RatingValue); }}
            className={`h-9 w-9 inline-flex items-center justify-center text-sm font-semibold border rounded-md tabular-nums transition-colors ${
              selected
                ? pillClass(n)
                : "bg-transparent text-zinc-500 border-zinc-800 hover:bg-zinc-900 hover:text-emerald-300 hover:border-emerald-500/30"
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
